import { Action, ActionReducer } from "@ngrx/store";
import { merge } from "lodash";
import { environment } from "src/environments/environment";
import { readStorageJson, writeStorageJson } from "src/app/shared/utils/safe-storage";
import { setLastPersistAction } from "src/app/shared/utils/storage-quota-diag";

/**
 * Persist the state blob, degrading silently on any storage failure (see `safe-storage`).
 *
 * <p>This runs INSIDE the reducer, i.e. inside NgRx's dispatch chain, and NgRx builds state as a
 * scan over the action stream. An exception thrown from here therefore does not merely lose one
 * write -- it errors the state observable and kills the store for the remaining life of the page:
 * selectors stop emitting, later dispatches stop reducing, and the UI is frozen until a full
 * reload. Persistence is a convenience and the store is the source of truth, so a failed write
 * must cost the snapshot, never the session.
 *
 * <p>The write can fail for reasons entirely outside this app's control and with storage nearly
 * empty: Safari private windows, "block all cookies", disk pressure, or evicted site data all
 * surface as a throwing {@code setItem}. Reported in production as a "The quota has been exceeded."
 * snackbar followed by a dead page that only reload cured.
 *
 * <p>Note the write is NOT latched off after a failure: it is retried on the next action, because a
 * refusal can be transient (freed disk, re-granted quota) and the cost of retrying is one refused
 * call per dispatch.
 */
function setSavedState(state: any, localStorageKey: string) {
  writeStorageJson('local', localStorageKey, stripTransientSlices(state));
}

/**
 * Drop the slices that must never reach the blob, on the WRITE side.
 *
 * <p>`conf.route` is the ACTIVE ROUTE'S CONFIG -- a `RouteConf`, not state. It cannot round-trip
 * and was never meant to: it is mostly component CLASSES and callbacks, which `JSON.stringify`
 * drops silently, so what came back was a mangled shell of the route the user happened to be on
 * last session, merged over an initial state that correctly says `route: null`. Every navigation
 * replaces it through `ConfChange` regardless.
 *
 * <p>And it could do worse than round-trip badly: a `RouteConf` may legitimately hold a LIVE
 * OBSERVABLE. `TableConfig.groupRow.changes$` is one -- the signal that tells a table to re-derive
 * which rows exist -- and the moment something subscribes to it, RxJS's own teardown linkage
 * (`SafeSubscriber._finalizers` -> `Subscription._parentage`) makes the graph CYCLIC. `stringify`
 * then throws, `writeStorageJson` reports a serialize failure, and NOTHING is persisted at all:
 * one screen's config silently costs the whole app its snapshot, filters and selections included.
 * Reported from the grouped orders dashboard. Stripping the route removes the class, not just that
 * instance -- any future conf may carry an Observable, and none of them belongs in a blob.
 *
 * <p>`shop.cartTotals` is a post-mutation aggregate refresh: brand/category subtotals plus the
 * order's payment info, re-fetched from the server after every line-level change. It is re-derived
 * on the next sync and the panel reloads its own summary on navigation, so nothing reads it back
 * across a page life. Persisting it would re-serialize the group array on EVERY non-router action,
 * in the same blob a quota refusal kills the store over.
 *
 * <p>Stripped here rather than only in {@link getSavedState} because the read side would still
 * leave the write cost — and the write cost is the whole reason. The state object itself is never
 * mutated: the store owns it, and a reducer that hands out a nulled slice would blank the panel.
 * Two shallow copies per dispatch is far cheaper than stringifying what they omit.
 */
function stripTransientSlices(state: any): any {
  let next = state;
  if (next && typeof next === 'object' && next.conf && next.conf.route != null) {
    const conf = { ...next.conf };
    delete conf.route;
    next = { ...next, conf };
  }
  if (!next || typeof next !== 'object' || !next.shop || next.shop.cartTotals == null) {
    return next;
  }
  const shop = { ...next.shop };
  delete shop.cartTotals;
  return { ...next, shop };
}
function getSavedState(localStorageKey: string): any {
  // Reads throw in the same conditions writes do, and this one runs on the init action -- an
  // unguarded throw here kills the store at boot, before the app has rendered anything.
  // A non-object payload (a stale format, or a key another feature overwrote) would break the
  // caller, which assigns onto the result -- and assigning onto a primitive throws in strict mode.
  const parsed: any = readStorageJson<any>('local', localStorageKey, null);
  if (!parsed || typeof parsed !== 'object') {
    return {};
  }
  // NEVER rehydrate the auth slice from localStorage. Identity must come from the
  // refresh-cookie flow at boot (AuthRefreshService.bootstrap); the bearer lives in
  // AccessTokenHolder, not the store. A pre-refactor build persisted an auth slice
  // that may contain a stale `token` field or `isAuthenticated=true` claim -- the
  // deep merge below would graft those onto the reducers EMPTY_AUTH for the window
  // between store creation (the @ngrx/store/init action, first action seen by this
  // metareducer) and the bootstrap dispatch of FULL EMPTY_AUTH. Stripping the slice
  // here closes that window entirely.
  if (parsed && typeof parsed === 'object') {
    delete parsed.auth;
    // Same reasoning for the twoFactor slice: enrollmentRequired is session-derived identity state,
    // not a durable preference. It is re-established from the refreshed token at boot
    // (AuthRefreshService derives it via decodeAuthToken(token).enrollment) and set from the login
    // response body. Rehydrating a stale `enrollmentRequired=true` here would mis-route the next
    // session into the enrollment wizard; stripping it keeps the slice genuinely in-memory, as its
    // callers assume.
    delete parsed.twoFactor;
    // Same reasoning for the router slice (StoreRouterConnectingModule persists `state.url` into this
    // same blob). It is transient navigation state, rebuilt by the router feed on the first navigation
    // of the new session — never a durable value to rehydrate. Grafting a stale `/app/...` url back at
    // boot would make selectIsOnAppRoute (hence selectIsFullyAuthenticated) read true the instant the
    // refresh-cookie flow flips isAuthenticated, firing the per-login userSettings loaders — with the
    // restricted enrollment token that can't reach them, so they 401 and tear the session down before
    // the real navigation to /auth/enroll runs. Stripping it keeps the slice genuinely in-memory.
    delete parsed.router;
    // `shop.cartTotals` is stripped on the write side (see stripTransientSlices), so a blob this
    // build produced never carries it. This clears one an OLDER build left behind: a stale set of
    // aggregates merged back at boot would be patched onto whichever order the panel loads next,
    // and applyTotals' order-id guard only rejects it once the summary has arrived.
    if (parsed.shop && typeof parsed.shop === 'object') {
      delete parsed.shop.cartTotals;
    }
    // `conf.route` is stripped on the write side too (see stripTransientSlices), so a blob this
    // build produced never carries it. This clears one an OLDER build left behind: a route object
    // whose components and callbacks JSON dropped, merged over an initial state that correctly says
    // `route: null`, leaves every ambient selector reading last session's route code until the
    // first navigation dispatches ConfChange.
    if (parsed.conf && typeof parsed.conf === 'object') {
      delete parsed.conf.route;
    }
  }
  return parsed || {};
}


// the keys from state which we'd like to save.
//const stateKeys = ['layout.theme'];
/**
 * The one localStorage key holding the persisted store snapshot. Exported so nothing hardcodes a
 * version-pinned copy of it: a literal `'galactus_ui.15.0.0_stat '` elsewhere in the tree kept
 * pointing at the pre-bump blob after `appVersion` moved to 15.0.1 and silently became a no-op.
 * (The trailing space is part of the key as shipped -- keep it, changing it orphans every user's
 * persisted state.)
 */
export const STORE_STATE_STORAGE_KEY = environment.appVersion + '_stat ';
const localStorageKey = STORE_STATE_STORAGE_KEY;

/**
 * A one-way latch that stops the blob being written for the remaining life of the DOCUMENT.
 *
 * There is exactly one caller and it is not an optimisation: the kiosk wizard's closing step hands
 * the iPad over to a shop counter, and removing the blob is not enough on its own. `clearSession()`
 * navigates, `router.navigateByUrl` is async, and the `/auth` module is lazily loaded -- so its
 * `import()` can resolve after the sweep has run, the resolve makes `AppComponent` dispatch
 * `Title`/`BackLink` (neither prefixed `@ngrx/router-store/`), and this metareducer writes the blob
 * straight back FROM THE LIVE STORE, where `conf.dynaData` still holds the outgoing staff user's
 * filters and selections. `location.replace()` does not stop the current document -- it starts a
 * navigation and script keeps running until the new one commits -- so a reload does not win that
 * race either; it only makes it short.
 *
 * Removing the key can therefore never be made safe by ordering alone. Not writing it can.
 *
 * One-way on purpose: nothing may turn persistence back on in a document whose store still holds
 * the outgoing user's state. {@link resumeStatePersistenceForTests} exists solely so a spec does not
 * poison the rest of an order-randomised run.
 */
let persistenceSuspended = false;

export function suspendStatePersistence(): void {
  persistenceSuspended = true;
}

export function isStatePersistenceSuspended(): boolean {
  return persistenceSuspended;
}

/** Spec-only reset for the module-level latch above. Never call this from app code. */
export function resumeStatePersistenceForTests(): void {
  persistenceSuspended = false;
}


export function storageMetaReducer<S, A extends Action = Action>(reducer: ActionReducer<S, A>) {
  let onInit = true; // after load/refresh…
  return function (state: S, action: A): S {
    // reduce the nextState.
    // ''
    const nextState = reducer(state, action);
    // init the application state.
    if (onInit) {
      onInit = false;
      const savedState = getSavedState(localStorageKey);
      savedState.init = false;
      // Strip transient UI state from the rehydrated snapshot. These slices
      // describe per-session UI conditions (loaders, progress bars), not
      // persistent data; rehydrating them strands the UI in a state nothing
      // alive will reset (e.g. ngx-ui-loader stuck on after a refresh because
      // layout.loading.block was true when the previous tab last saved state).
      if (savedState.layout) {
        delete savedState.layout.loading;
      }
      return merge(nextState, savedState);
    }
    // Router-store actions (ROUTER_REQUEST/NAVIGATION/NAVIGATED/…) fire on EVERY navigation but carry
    // no durable state. Persisting on them would rewrite the whole shared localStorage blob — including
    // the user-mutable `layout.sidenav` slice — on each route change, so a tab that merely navigates
    // would stamp its own (possibly stale) layout snapshot over a sibling tab's sidenav toggle on the
    // next rehydrate. Skip the write for them; this restores the pre-router-connect behaviour where a
    // pure navigation dispatched nothing. The router slice still lives in-memory for selectors and is
    // stripped from any rehydrated snapshot anyway (see getSavedState).
    if (action.type.startsWith('@ngrx/router-store/')) {
      return nextState;
    }
    // The document is being handed to somebody else (see `suspendStatePersistence`). Every dispatch
    // from here on -- including the ones the pending lazy `/auth` chunk fires as it resolves during
    // the unload window -- reduces normally and writes nothing.
    if (persistenceSuspended) {
      return nextState;
    }
    // save the next state to the application storage.
    //const stateToSave = pick(nextState, stateKeys);
    // Stamp the action first: the write below fails silently two layers down, inside `safe-storage`,
    // which cannot know what NgRx is. The action type is what makes a captured failure reproducible
    // -- it names the interaction the user performed. The KEY goes with it so the diagnostic only
    // attributes the action to a failure on THIS key: every other storage user in the app reports
    // through the same sink, and a preference or diagnostic-ring failure labelled with the last
    // persisted action would send an investigation after the wrong interaction.
    setLastPersistAction(action.type, localStorageKey);
    setSavedState(nextState, localStorageKey);
    return nextState;
  };
}
